import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import Sidebar from '../Sidebar/Sidebar';
import { NoPermission } from '../NoPermission/NoPermission';
import { Project } from '../Project/Project';
import {
  getDigisData,
  getDoitData,
  postDigisEntry,
  refreshDoitUnloggedUsers,
} from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { getAllUsers, getUnloggedUsers } from '../../firebase';
import {
  dayAgo,
  isMondayToday,
  isSundayToday,
  lastFriday,
  today,
} from '../../helpers/dates';
import { blacklistedUser } from '../../helpers/firebase';
import Loader from '../Loader/Loader';
import { extractEntryDate } from '../../helpers/common';

import './Projects.scss';

const Projects = () => {
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [currentDay, setCurrentDay] = useState(today);
  const [calendarDay, setCalendarDay] = useState(new Date());
  const [isSideBarVisible, setSideBarVisible] = useState(false);
  const [isReload, setIsReload] = useState(true);
  const [usersData, setUsersData] = useState([]);
  const [unloggedUsers, setUnloggedUsers] = useState(null);
  const [digisData, setDigisData] = useState(null);
  const [doItData, setDoItData] = useState(null);
  // transfer locks
  const [isTransferringAll, setIsTransferringAll] = useState(false);
  const [isTransferringSingle, setIsTransferringSingle] = useState(false);
  // calendar states
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedRange, setSelectedRange] = useState({
    from: undefined,
    to: undefined,
  });
  const [isExporting, setIsExporting] = useState(false);
  // refs to avoid stale closure & allow immediate guard without re-render cost
  const transferringAllRef = useRef(false);
  const transferringSingleRef = useRef(false);

  const handleSideBarClick = () => {
    setSideBarVisible((prevState) => !prevState);
  };

  const handleDayClick = (day) => {
    const currentDate = day.toLocaleDateString('en-US').split('/');
    const newDay = `${currentDate[2]}-${currentDate[0].padStart(
      2,
      '0'
    )}-${currentDate[1].padStart(2, '0')}`;
    setCurrentDay(newDay);
    setCalendarDay(day);
    handleSideBarClick();
  };

  const formatDateForApi = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchAndUpdateData = async () => {
    setIsLoading(true);

    try {
      const userDoItEmail =
        currentUser?.email === 'd.petrakovskyi@digiscorp.com'
          ? 'd.petrakovskyi@do-it.co'
          : currentUser?.email;

      if (!isSundayToday) {
        if (
          (!isMondayToday && currentDay === dayAgo) ||
          (isMondayToday && currentDay === lastFriday)
        ) {
          const [
            receivedDigisData,
            receivedDoItData,
            dbUsers,
            dbUnloggedUsers,
          ] = await Promise.all([
            getDigisData(currentUser?.email, currentDay),
            getDoitData(userDoItEmail, currentDay),
            getAllUsers(),
            getUnloggedUsers(isMondayToday ? lastFriday : dayAgo),
          ]);

          setDoItData(receivedDoItData);
          setDigisData(receivedDigisData);
          setUsersData(dbUsers);
          setUnloggedUsers(dbUnloggedUsers);
          return;
        }
      }

      const [receivedDigisData, receivedDoItData, dbUsers] = await Promise.all([
        getDigisData(currentUser?.email, currentDay),
        getDoitData(userDoItEmail, currentDay),
        getAllUsers(),
      ]);

      setDoItData(receivedDoItData);
      setDigisData(receivedDigisData);
      setUsersData(dbUsers);
    } catch (error) {
      toast.error('Failed to fetch project data.');
      console.error('Error fetching project data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser && currentUser?.email) {
      fetchAndUpdateData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDay, currentUser]);

  const handleAddToUsersState = (projectId, userObj) => {
    setUsersData((prevState) => {
      const projectIndex = prevState.findIndex(
        (record) => +record?.projectId === +projectId
      );
      if (projectIndex === -1) {
        return [...prevState, { projectId: +projectId, users: [userObj] }];
      } else {
        const updatedUsers = [...prevState[projectIndex].users, userObj];
        return prevState.map((record, index) =>
          index === projectIndex ? { ...record, users: updatedUsers } : record
        );
      }
    });
  };

  const handleRemoveFromUsersState = (projectId, userObj) => {
    setUsersData((prevState) => {
      return prevState.map((record) =>
        +record.projectId === +projectId
          ? {
              ...record,
              users: record.users.filter((user) => +user.id !== +userObj.id),
            }
          : record
      );
    });
  };

  const handleUpdateUsersState = (projectId, updatedData) => {
    setUsersData((prevState) =>
      prevState.map((record) =>
        +record.projectId === +projectId
          ? { ...record, users: updatedData }
          : record
      )
    );
  };

  const handleRemoveUnloggedUser = (userId) => {
    setUnloggedUsers((prevState) => ({
      ...prevState,
      users: prevState.users.filter((u) => +u.id !== +userId),
    }));
  };

  const handleRefreshUnloggedUsers = async () => {
    try {
      toast.loading('Refreshing...');
      await refreshDoitUnloggedUsers({
        date: isMondayToday ? lastFriday : dayAgo,
      });
      await fetchAndUpdateData();

      toast.dismiss();
      toast.success('Refreshed successfully');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to refresh unlogged users.');
      console.error('Error refreshing unlogged users:', error);
    }
  };

  const handleMoveAllUsersToWhiteTick = async (event) => {
    event.preventDefault();
    if (transferringAllRef.current) return;
    transferringAllRef.current = true;
    setIsTransferringAll(true);

    try {
      if (!doItData || !digisData) {
        toast.error('Data is not ready yet.');
        return;
      }

      const tasksToPost = [];

      // Loop through all DO-IT projects and find exact-name match in DIGIS
      for (const project of doItData.projects || []) {
        if (
          project.name.toLowerCase().trim() === 'parkopoly' ||
          project.name.toLowerCase().trim() === 'voicetutor' ||
          project.name.toLowerCase().trim() === 'zendu' ||
          project.name.toLowerCase().trim() === 'tener.ai' ||
          project.name.toLowerCase().trim() === 'hubspot' ||
          project.name.toLowerCase().trim() === 'ongage' ||
          project.name.toLowerCase().trim() === 'v1 scope development'
        ) {
          continue;
        }

        const currentDigisProject = (digisData.projects || []).find(
          (dp) =>
            dp.name.toLowerCase().trim() === project.name.toLowerCase().trim()
        );

        if (!currentDigisProject) {
          continue; // no matching project in DIGIS
        }

        // Filter entries belonging to this DO-IT project and not blacklisted
        const filteredEntriesToSend = (doItData.entries || []).filter(
          (entry) => {
            return (
              +entry.project_id === +project.id &&
              !blacklistedUser(
                usersData,
                entry.entryDetails.user_id,
                project.id
              )
            );
          }
        );

        if (!filteredEntriesToSend.length) continue;

        // Prepare posts for this project
        filteredEntriesToSend.forEach((entry) => {
          const currentDoitUser = (doItData.users || []).find(
            (user) => entry.entryDetails.user_id === user.id
          );

          const currentDigisUser = (digisData.users || []).find(
            (user) =>
              user.email.split('@')[0].toLowerCase() ===
                currentDoitUser?.email?.split('@')?.[0]?.toLowerCase() ||
              (user.first_name.toLowerCase().trim() ===
                currentDoitUser?.first_name?.toLowerCase()?.trim() &&
                user.last_name.toLowerCase().trim() ===
                  currentDoitUser?.last_name?.toLowerCase()?.trim())
          );

          const currTask = (digisData.tasks || []).find(
            (task) =>
              task.project_id === currentDigisProject.id &&
              task.name.toLowerCase().slice(0, 2) ===
                entry.task_name.toLowerCase().slice(0, 2)
          );

          const duplicate = (digisData.entries || []).some(
            (digisEntry) =>
              (digisEntry.entryDetails.user_id === currentDigisUser?.id &&
                digisEntry.entryDetails.date === currentDay &&
                digisEntry.project_id === currentDigisProject.id) ||
              (digisEntry.entryDetails.user_id === currentDigisUser?.id &&
                digisEntry.project_id === currentDigisProject.id &&
                filteredEntriesToSend.some(
                  (entryCandidate) =>
                    entryCandidate.entryDetails.notes.toLowerCase().trim() ===
                    digisEntry.entryDetails.notes.toLowerCase().trim()
                ))
          );

          if (currTask && currentDigisUser && !duplicate) {
            tasksToPost.push(
              postDigisEntry({
                date: currentDay,
                hours: entry.entryDetails.hours,
                notes: entry.entryDetails.notes,
                task_id: currTask.id,
                user_id: currentDigisUser.id,
              })
            );
          }
        });
      }

      if (tasksToPost.length) {
        await Promise.all(tasksToPost);
        await fetchAndUpdateData();
      }
    } catch (error) {
      console.error('Bulk transfer failed:', error);
      toast.error('Failed to transfer entries');
    } finally {
      transferringAllRef.current = false;
      setIsTransferringAll(false);
    }
  };

  const handleMoveUsersToWhiteTick = async (
    event,
    { project, currentDigisProject }
  ) => {
    event.preventDefault();
    // Guard: prevent concurrent bulk transfers
    if (transferringAllRef.current) return;
    transferringAllRef.current = true;
    setIsTransferringAll(true);

    if (doItData && digisData) {
      const filteredEntriesToSend = doItData.entries.filter((entry) => {
        return (
          +entry.project_id === +event.target.id &&
          !blacklistedUser(usersData, entry.entryDetails.user_id, project.id)
        );
      });

      if (filteredEntriesToSend.length >= 1) {
        try {
          const tasksToPost = [];
          filteredEntriesToSend.forEach((entry) => {
            const currentDoitUser = doItData.users.find(
              (user) => entry.entryDetails.user_id === user.id
            );

            const currentDigisUser = digisData.users.find(
              (user) =>
                user.email.split('@')[0].toLowerCase() ===
                  currentDoitUser?.email?.split('@')?.[0]?.toLowerCase() ||
                (user.first_name.toLowerCase().trim() ===
                  currentDoitUser?.first_name?.toLowerCase()?.trim() &&
                  user.last_name.toLowerCase().trim() ===
                    currentDoitUser?.last_name?.toLowerCase()?.trim())
            );

            const currTask = digisData.tasks.find(
              (task) =>
                task.project_id === currentDigisProject.id &&
                task.name.toLowerCase().slice(0, 2) ===
                  entry.task_name.toLowerCase().slice(0, 2)
            );

            const duplicate = digisData.entries.some(
              (digisEntry) =>
                (digisEntry.entryDetails.user_id === currentDigisUser?.id &&
                  digisEntry.entryDetails.date === currentDay &&
                  digisEntry.project_id === currentDigisProject.id) ||
                (digisEntry.entryDetails.user_id === currentDigisUser?.id &&
                  digisEntry.project_id === currentDigisProject.id &&
                  filteredEntriesToSend.some(
                    (entryCandidate) =>
                      entryCandidate.entryDetails.notes.toLowerCase().trim() ===
                      digisEntry.entryDetails.notes.toLowerCase().trim()
                  ))
            );

            if (currTask && currentDigisUser && !duplicate) {
              tasksToPost.push(
                postDigisEntry({
                  date: currentDay,
                  hours: entry.entryDetails.hours,
                  notes: entry.entryDetails.notes,
                  task_id: currTask.id,
                  user_id: currentDigisUser.id,
                })
              );
            }
          });

          if (tasksToPost.length) {
            await Promise.all(tasksToPost);
            await fetchAndUpdateData();
          }
        } finally {
          transferringAllRef.current = false;
          setIsTransferringAll(false);
        }
      } else {
        transferringAllRef.current = false;
        setIsTransferringAll(false);
      }
    } else {
      toast.error('Data is not ready yet.');
      transferringAllRef.current = false;
      setIsTransferringAll(false);
    }
  };

  const handleMoveUserToWhiteTick = (event, { entry, currentDigisProject }) => {
    event.preventDefault();
    event.stopPropagation();
    if (transferringSingleRef.current) return; // guard
    transferringSingleRef.current = true;
    setIsTransferringSingle(true);

    if (doItData?.users?.length > 0 && digisData?.users?.length > 0) {
      const filteredEntriesToSend = [entry];

      if (filteredEntriesToSend.length >= 1) {
        filteredEntriesToSend.forEach((entry) => {
          const currentDoitUser = doItData.users.find(
            (user) => entry.entryDetails.user_id === user.id
          );

          const currentDigisUser = digisData.users.find(
            (user) =>
              user.email.split('@')[0].toLowerCase() ===
                currentDoitUser.email.split('@')[0].toLowerCase() ||
              (user.first_name.toLowerCase().trim() ===
                currentDoitUser.first_name.toLowerCase().trim() &&
                user.last_name.toLowerCase().trim() ===
                  currentDoitUser.last_name.toLowerCase().trim())
          );

          const currTask = digisData.tasks.find(
            (task) =>
              task.project_id === currentDigisProject.id &&
              task.name.toLowerCase().slice(0, 2) ===
                entry.task_name.toLowerCase().slice(0, 2)
          );

          const duplicate = digisData.entries.some(
            (entry) =>
              (entry.entryDetails.user_id === currentDigisUser.id &&
                entry.entryDetails.date === currentDay &&
                entry.project_id === currentDigisProject.id) ||
              (entry.entryDetails.user_id === currentDigisUser.id &&
                entry.project_id === currentDigisProject.id &&
                filteredEntriesToSend.some(
                  (digisEntry) =>
                    digisEntry.entryDetails.notes.toLowerCase().trim() ===
                    entry.entryDetails.notes.toLowerCase().trim()
                ))
          );

          if (duplicate) {
            alert('Duplicate data is discovered!');
            return;
          }

          if (currTask && currentDigisUser && !duplicate) {
            const data = {
              date: currentDay,
              hours: entry.entryDetails.hours,
              notes: entry.entryDetails.notes,
              task_id: currTask.id,
              user_id: currentDigisUser.id,
            };

            const confirmation = window.confirm(
              `Confirm transfer selected entry with Task: ${data.notes}, Time: ${data.hours}.`
            );

            if (!confirmation) return;

            postDigisEntry(data)
              .then(() => fetchAndUpdateData())
              .finally(() => {
                transferringSingleRef.current = false;
                setIsTransferringSingle(false);
              });
          } else {
            transferringSingleRef.current = false;
            setIsTransferringSingle(false);
          }
        });
      }
    } else {
      toast.error('Data is not ready yet.');
      transferringSingleRef.current = false;
      setIsTransferringSingle(false);
    }
  };

  const handleEntryChange = ({ id, value, type, doItData }) => {
    const changedEntries = doItData.entries.map((entry) => {
      if (entry.entryDetails.id === id) {
        return {
          ...entry,
          entryDetails: { ...entry.entryDetails, [type]: value },
        };
      }
      return entry;
    });

    setDoItData((prevData) => ({ ...prevData, entries: changedEntries }));
  };

  const handleDownloadAllUnloggedUsers = async () => {
    if (!selectedRange.from) {
      toast.error('Please select a date');
      return;
    }

    setIsExporting(true);

    const toastId = toast.loading('Exporting data...');

    try {
      const startDate = formatDateForApi(selectedRange.from);
      const endDate = selectedRange.to
        ? formatDateForApi(selectedRange.to)
        : startDate;
      const userDoItEmail =
        currentUser?.email === 'd.petrakovskyi@digiscorp.com'
          ? 'd.petrakovskyi@do-it.co'
          : currentUser?.email;

      // Fetch DO-IT data for the full range (inclusive)
      const doItDataRange = await getDoitData(userDoItEmail, startDate, endDate);

      if (!doItDataRange?.entries?.length || !usersData?.length) {
        toast.dismiss(toastId);
        toast.error('Data not ready for export');
        setIsExporting(false);
        return;
      }

      // Build list of all dates in the range (inclusive)
      const datesInRange = [];
      const cursor = new Date(selectedRange.from);

      while (cursor <= selectedRange.to) {
        datesInRange.push(formatDateForApi(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }

      const projectNameById = {};

      (doItDataRange.projects || []).forEach((p) => {
        projectNameById[+p.id] = p.name;
      });

      usersData.forEach((record) => {
        if (!projectNameById[+record.projectId]) {
          const userWithName = (record.users || []).find((u) => u.projectName);
          if (userWithName?.projectName) {
            projectNameById[+record.projectId] = userWithName.projectName;
          }
        }
      });

      // Index entries by project + date -> Set(userId)
      const entriesByProjectDate = new Map(); // key `${projectId}|${date}` -> Set(userIds)

      for (const entry of doItDataRange.entries) {
        const d = extractEntryDate(entry);

        if (!d) continue;

        const key = `${entry.project_id}|${d}`;

        if (!entriesByProjectDate.has(key)) {
          entriesByProjectDate.set(key, new Set());
        }

        entriesByProjectDate.get(key).add(+entry.entryDetails.user_id);
      }

      const rows = [];

      for (const dateStr of datesInRange) {
        for (const record of usersData) {
          const projectIdNum = +record.projectId;

          if (
            projectIdNum === 1827338 || // "parkopoly"
            projectIdNum === 2531067 || // "voice tutor"
            projectIdNum === 1747371 || // "zendu"
            projectIdNum === 2561012 || // "tener ai"
            projectIdNum === 2043865 || // "hubspot"
            projectIdNum === 2150819 || // "ongage"
            projectIdNum === 2571261    // "v1 scope development"
          ) {
            continue;
          }
          
          const key = `${projectIdNum}|${dateStr}`;
          const loggedSet = entriesByProjectDate.get(key) || new Set();

          // Get the logged user IDs for this project on this date
          const loggedUserIdsForDate = Array.from(loggedSet);

          // Filter users just like remainingProjectUsers does in Project component
          const unloggedUsersForDate = (record.users || []).filter(
            (u) => !loggedUserIdsForDate.includes(+u.id)
          );

          for (const user of unloggedUsersForDate) {
            // Optional: Add additional filters here if needed
            // Skip blacklisted users:
            if (blacklistedUser(usersData, user.id, projectIdNum)) continue;

            rows.push({
              Date: dateStr,
              ProjectID: projectIdNum,
              ProjectName: projectNameById[projectIdNum] || '',
              FullName: user.fullName,
            });
          }
        }
      }

      if (!rows.length) {
        toast.dismiss(toastId);
        toast.error('No unlogged users for the selected range');
        setIsExporting(false);
        return;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      const safeSheetName =
        `${startDate}_${endDate}`.replace(/[^a-z0-9]/gi, '_').slice(0, 31) ||
        'Unlogged';
      XLSX.utils.book_append_sheet(wb, ws, safeSheetName);
      const fileName = `unlogged_users_${startDate}_to_${endDate}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast.dismiss(toastId);
      toast.success('Excel exported successfully');
      setSelectedRange({ from: undefined, to: undefined });
      setShowDatePicker(false);
    } catch (error) {
      console.error('Export failed:', error);
      toast.dismiss(toastId);
      toast.error('Failed to export Excel');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <div className="projectsBoard">
        <div className="projectsBoard__container">
          <Sidebar
            handleDayClick={handleDayClick}
            calendarDay={calendarDay}
            isSideBarVisible={isSideBarVisible}
            handleSideBarClick={handleSideBarClick}
            isExporting={isExporting}
            handleDownloadAllUnloggedUsers={handleDownloadAllUnloggedUsers}
            handleMoveAllUsersToWhiteTick={handleMoveAllUsersToWhiteTick}
            showDatePicker={showDatePicker}
            setShowDatePicker={setShowDatePicker}
            selectedRange={selectedRange}
            setSelectedRange={setSelectedRange}
          />
          {isLoading ? (
            <Loader />
          ) : doItData?.projects?.length > 0 &&
            digisData?.projects?.length > 0 ? (
            <div className="projectsBoard__body">
              {doItData.projects
                .filter((project) =>
                  digisData.projects.some(
                    (digisProject) =>
                      digisProject.name.toLowerCase().trim() ===
                      project.name.toLowerCase().trim()
                  )
                )
                .map((project) => {
                  const currentDigisProject = digisData.projects.find(
                    (digisProject) =>
                      digisProject.name.toLowerCase().trim() ===
                      project.name.toLowerCase().trim()
                  );
                  return (
                    <Project
                      key={`${project.id}-${project.name}`}
                      project={project}
                      digisData={digisData}
                      doItData={doItData}
                      handleEntryChange={handleEntryChange}
                      currDay={currentDay}
                      setIsReload={setIsReload}
                      isReload={isReload}
                      currentDigisProject={currentDigisProject}
                      usersData={usersData}
                      unloggedUsers={unloggedUsers}
                      handleMoveUsersToWhiteTick={handleMoveUsersToWhiteTick}
                      handleMoveUserToWhiteTick={handleMoveUserToWhiteTick}
                      refreshUnloggedUsers={handleRefreshUnloggedUsers}
                      addToUsersState={handleAddToUsersState}
                      updateUsersState={handleUpdateUsersState}
                      removeFromUsersState={handleRemoveFromUsersState}
                      removeUnloggedUser={handleRemoveUnloggedUser}
                      isTransferringAll={isTransferringAll}
                      isTransferringSingle={isTransferringSingle}
                    />
                  );
                })}
            </div>
          ) : (
            <NoPermission />
          )}
        </div>
      </div>
    </>
  );
};

export default Projects;
